import React from 'react';

/**
 * Pricing, presented as a comparison table rather than as two cards.
 *
 * Two plans differing on exactly two axes (seats and dedicated support) is a
 * comparison, and a comparison reads better as a grid than as two stacks the
 * eye has to shuttle between: the table puts both values for a given attribute
 * on one line, so the difference is where your eye already is. It is also
 * denser — the card layout spent most of its height on padding, a gradient
 * banner and a badge, none of which carried information.
 *
 * House style, matching the catalogue pages: monospace for every label, tag and
 * numeral, hairline borders, square corners, no gradients and no transitions —
 * states switch instantly, so there is nothing for prefers-reduced-motion to
 * suppress.
 *
 * Removed rather than restyled: the "90% CHEAPER THAN COMPETITORS" badge and
 * the struck-through "$2,850+ elsewhere" comparison. Both were unsourced, named
 * no competitor and cited no basis; the props are gone so they cannot return.
 * Prices, seat counts, the 14-day trial and the 30-day guarantee are unchanged.
 */

/**
 * A bordered panel with its label notched into the top border, as on the
 * catalogue pages. The label sits on the page ground so it interrupts the
 * hairline — it therefore assumes a `#0d1117` ground.
 *
 * Exported because DatasetList renders the same panel alongside this table.
 */
export function Panel({
  label,
  children,
  className = '',
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`relative border border-gray-800 ${className}`}>
      <span className="absolute -top-[7px] left-3 bg-[#0d1117] px-1.5 font-mono text-[11px] uppercase tracking-wider text-gray-400">
        {label}
      </span>
      {children}
    </div>
  );
}

export interface PlanColumn {
  /** Column heading, e.g. "Professional". */
  title: string;
  /** Dollars per month. Rendered monospace with tabular figures. */
  price: number;
  ctaText: string;
  href: string;
  /** Rendered as a plain monospace tag, not a badge. */
  mostPopular?: boolean;
}

export interface PlanCell {
  /** `true`/`false` render as yes/no; strings render verbatim. */
  value: string | boolean;
  /** Figures get tabular figures so digits line up down the column. */
  numeric?: boolean;
  /** Short qualifier under the value, e.g. "different IPs". */
  note?: string;
}

export interface PlanRow {
  label: string;
  /** One cell per plan, in the same order as `plans`. */
  cells: PlanCell[];
}

export interface PlanComparisonTableProps {
  plans: PlanColumn[];
  rows: PlanRow[];
  /** Plain lines of text under the table — trial, guarantee. */
  terms: string[];
}

/** yes/no rather than icons: it copies, translates and screen-reads cleanly. */
function Cell({ cell }: { cell: PlanCell }) {
  if (typeof cell.value === 'boolean') {
    return (
      <span
        className={`font-mono text-xs ${
          cell.value ? 'text-white' : 'text-gray-400'
        }`}
      >
        {cell.value ? 'yes' : 'no'}
      </span>
    );
  }
  return (
    <>
      <span
        className={`font-mono text-xs text-white ${
          cell.numeric ? 'tabular-nums' : ''
        }`}
      >
        {cell.value}
      </span>
      {cell.note && (
        <span className="mt-0.5 block font-mono text-[11px] leading-tight text-gray-400">
          {cell.note}
        </span>
      )}
    </>
  );
}

export function PlanComparisonTable({
  plans,
  rows,
  terms,
}: PlanComparisonTableProps) {
  return (
    // min-w-0 lets the panel shrink below the table's min-width: as a grid item
    // it would otherwise size to its content and push the page sideways.
    <Panel label="plans" className="min-w-0">
      {/* The table scrolls inside this container; the page never scrolls sideways. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[460px] border-collapse text-sm">
          <caption className="sr-only">
            Sov.ai plan comparison: price, seats and included features per plan.
          </caption>
          <thead>
            <tr className="border-b border-gray-800">
              {/* Empty corner cell above the attribute column. */}
              <th scope="col" className="w-[38%] px-3 pb-3 pt-6" />
              {plans.map(plan => (
                <th
                  key={plan.title}
                  scope="col"
                  className="border-l border-gray-800 px-3 pb-3 pt-6 text-left align-bottom font-normal"
                >
                  {plan.mostPopular && (
                    <span className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-blue-400">
                      most popular
                    </span>
                  )}
                  <span className="block font-mono text-[11px] uppercase tracking-wider text-gray-400">
                    {plan.title}
                  </span>
                  <span className="mt-1 block font-mono text-2xl tabular-nums text-white">
                    ${plan.price}
                    <span className="ml-1 font-mono text-xs tabular-nums text-gray-400">
                      /mo
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map(row => (
              <tr
                key={row.label}
                className="border-b border-dashed border-gray-800/70"
              >
                <th
                  scope="row"
                  className="px-3 py-2 text-left align-top font-mono text-xs font-normal text-gray-400"
                >
                  {row.label}
                </th>
                {row.cells.map((cell, index) => (
                  <td
                    key={plans[index]?.title ?? index}
                    className="border-l border-gray-800 px-3 py-2 align-top"
                  >
                    <Cell cell={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr>
              <td className="px-3 py-3" />
              {plans.map(plan => (
                <td
                  key={plan.title}
                  className="border-l border-gray-800 px-3 py-3 align-top"
                >
                  <a
                    href={plan.href}
                    className="block border border-gray-700 px-3 py-2 text-center font-mono text-xs text-white hover:border-blue-400 hover:bg-blue-500/15 hover:text-blue-300 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
                  >
                    {plan.ctaText}
                  </a>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Trial and guarantee: plain lines of text, no banner. */}
      <ul className="border-t border-gray-800 px-3 py-2">
        {terms.map(term => (
          <li key={term} className="font-mono text-[11px] text-gray-400">
            {term}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
